/**
 * Enhanced chat-based search interface.
 * 
 * This component provides an intuitive chat interface for natural language
 * article search with session management and real-time results.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { chatSearch, createSearchSession, clearSearchSession } from '../services/search';
import { useToast } from '../hooks/useToast';
import { stripMarkdownFormatting } from '../utils/markdown';

const ChatSearch = ({ onArticleClick, savedSearchState, onSearchStateCleared, className = '' }) => {
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasRestoredStateRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize session and focus input on mount
  useEffect(() => {
    if (savedSearchState && !hasRestoredStateRef.current) {
      // Restore saved search state once - convert timestamp strings back to Date objects
      const messagesWithDates = (savedSearchState.searchMessages || []).map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
      setMessages(messagesWithDates);
      setSessionId(savedSearchState.searchSessionId || null);
      hasRestoredStateRef.current = true;
      if (onSearchStateCleared) {
        onSearchStateCleared();
      }
    } else if (!savedSearchState && !hasRestoredStateRef.current) {
      // Show welcome message immediately
      setMessages([{
        id: 1,
        type: 'system',
        content: 'Hi! Ask me anything about your articles.',
        timestamp: new Date()
      }]);
      
      // Initialize session in background
      initializeSession();
    }
    
    // Focus input after modal opens
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []); // Empty dependency array - only run once on mount

  const initializeSession = async () => {
    try {
      const session = await createSearchSession();
      setSessionId(session.session_id);
      // Welcome message is now set immediately in useEffect, no need to add here
    } catch (error) {
      console.error('Failed to initialize session:', error);
      showError('Failed to initialize chat session');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!query.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query.trim(),
      timestamp: new Date()
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    const currentQuery = query.trim();
    setQuery('');
    setIsLoading(true);

    try {
      // Perform search
      const response = await chatSearch(currentQuery, sessionId);
      
      // Create system response
      const systemMessage = {
        id: Date.now() + 1,
        type: 'system',
        content: response.results.length > 0 
          ? `Found ${response.total_found} results in ${response.search_time_ms}ms`
          : 'No articles found matching your query.',
        timestamp: new Date(),
        searchResponse: response
      };

      setMessages(prev => [...prev, systemMessage]);
      
      if (response.results.length > 0) {
        showSuccess(`Found ${response.results.length} articles`);
      }
      
    } catch (error) {
      console.error('Search error:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: `Search failed: ${error.message}`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      showError(error.message);
    } finally {
      setIsLoading(false);
      // Refocus input for next query
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleNewChat = async () => {
    try {
      if (sessionId) {
        await clearSearchSession(sessionId);
      }
      
      // Create new session
      const session = await createSearchSession();
      setSessionId(session.session_id);
      
      // Clear messages and add welcome
      setMessages([{
        id: Date.now(),
        type: 'system',
        content: 'New chat started! Ask me about your articles.',
        timestamp: new Date()
      }]);
      
      showSuccess('New chat session created');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to start new chat:', error);
      showError('Failed to start new chat');
    }
  };

  const handleArticleClick = (e, articleId) => {
    e.preventDefault();
    
    // Save search state to session storage for restoration
    const searchState = {
      searchMessages: messages,
      searchSessionId: sessionId
    };
    sessionStorage.setItem('searchModalState', JSON.stringify(searchState));
    
    // Navigate with search state preserved
    navigate(`/article/${articleId}`, {
      state: {
        fromSearch: true,
        searchMessages: messages,
        searchSessionId: sessionId,
        returnPath: location.pathname
      }
    });

    // Close modal after navigation
    if (onArticleClick) {
      onArticleClick();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const renderSearchResults = (response) => {
    if (!response || !response.results) return null;

    return (
      <div className="mt-4 space-y-3">
        {response.results.map((result, index) => (
          <div key={index} className="bg-gray-50 hover:bg-gray-100 p-4 rounded-lg border transition-colors">
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <Link 
                  to={`/article/${result.article.id}`}
                  onClick={(e) => handleArticleClick(e, result.article.id)}
                  className="text-blue-600 hover:text-blue-800 font-medium text-base block"
                >
                  {result.article.title || 'Untitled Article'}
                </Link>
                <div className="flex items-center mt-1 space-x-2 text-xs text-gray-500">
                  <span className="font-medium">{result.article.domain}</span>
                  <span>•</span>
                  <span>{formatDate(result.article.publish_date || result.article.created_at)}</span>
                  {!result.article.is_read && (
                    <>
                      <span>•</span>
                      <span className="text-blue-600 font-medium">Unread</span>
                    </>
                  )}
                </div>
                {result.article.summary && (
                  <p className="text-gray-700 text-sm mt-2 line-clamp-3">
                    {stripMarkdownFormatting(result.article.summary).substring(0, 200)}
                    {result.article.summary.length > 200 && '...'}
                  </p>
                )}
              </div>
              <div className="text-right ml-4 flex-shrink-0">
                <div className="text-xs font-medium text-gray-500">
                  {(result.relevance_score * 100).toFixed(0)}% match
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 italic">
              {result.match_explanation}
            </p>
          </div>
        ))}
        
        {response.suggestions && response.suggestions.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-200">
            <div className="text-sm">
              <strong className="text-blue-800">Try these suggestions:</strong>
              <ul className="mt-2 space-y-1 text-blue-700">
                {response.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div key={message.id} className="flex flex-col">
            <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-2xl px-4 py-3 rounded-lg shadow-sm ${
                message.type === 'user' 
                  ? 'bg-blue-500 text-white' 
                  : message.type === 'error'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                <p className="text-sm">{message.content}</p>
                <p className={`text-xs mt-2 ${
                  message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
            
            {/* Render search results if present */}
            {message.searchResponse && (
              <div className="ml-0 mt-2">
                {renderSearchResults(message.searchResponse)}
              </div>
            )}
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-3 rounded-lg shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span className="text-sm text-gray-600">Searching your articles...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t bg-white p-4">
        <form onSubmit={handleSearch} className="flex space-x-3">
          <div className="flex-1">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask about your articles..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isLoading ? 'Searching...' : 'Search'}
          </button>
          <button
            type="button"
            onClick={handleNewChat}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            title="Start new chat"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatSearch;